/**
 * Checks one part of the patient form - the Medicare card, the DVA file number, or one health fund policy -
 * with Tyro, when its Validate button is pressed. Never on save.
 *
 * Works off the form as typed, so a new patient can be checked before they are saved. The provider number the
 * check goes under is the server's to choose: the user's own, or else one held at the practice.
 *
 * Holds each part's answer against the very object it is about (the medicare_details, the dva_details, the
 * policy), so the button in a section's header and the answer under it read the same state, and a part that is
 * cleared and typed in again starts fresh. A clean answer stamps last_verified_at on that object, which saves
 * with the patient; changing the details afterwards takes the stamp back off - see reconcile().
 */
app.service("PatientCoverCheckService", [
	"Session", "TyroAPI", "ExceptionAPI", "AUSTRALIAN_HEALTH_FUND_TYPE", "AUSTRALIAN_HEALTH_FUND_TYPE_TEST",
	function (Session, TyroAPI, ExceptionAPI, AUSTRALIAN_HEALTH_FUND_TYPE, AUSTRALIAN_HEALTH_FUND_TYPE_TEST) {
		const FUND_NAMES = AUSTRALIAN_HEALTH_FUND_TYPE.concat(AUSTRALIAN_HEALTH_FUND_TYPE_TEST);
		// DVA answers with the card type it holds, which the form records under its own names.
		const DVA_CARD_TYPES = { PTEC: "GOLD", STEC: "WHITE", RPBC: "ORANGE" };
		const DVA_CARD_LABELS = {
			PTEC: "Gold card",
			STEC: "White card",
			RPBC: "Orange card",
			PCC: "Pensioner Concession Card",
			CSHC: "Commonwealth Seniors Health Card",
		};

		// Keyed by the object each check is about. A WeakMap, so a patient the form has moved on from takes
		// its answers with it.
		const states = new WeakMap();

		const service = {
			canValidate: function () {
				return Session.hasPermission("PATIENT_COVER_LOOKUP");
			},

			// Whether this part has enough typed into it to ask about.
			hasDetails: function (patient, kind, policy) {
				const target = targetOf(patient, kind, policy);
				if (!target) return false;
				if (kind === "MEDICARE") return !!compact(target.card_number);
				if (kind === "DVA") return !!compact(target.file_number);
				return !!(target.health_fund_name && text(target.membership_number));
			},

			stateFor: function (patient, kind, policy) {
				const target = targetOf(patient, kind, policy);
				return target ? states.get(target) || null : null;
			},

			validate: function (patient, dateOfBirth, kind, policy) {
				const target = targetOf(patient, kind, policy);
				if (!target) return;
				const previous = states.get(target);
				if (previous && previous.checking) return;

				const state = { checking: true, error: null, funder: null, checkedKey: null, stamp: previous && previous.stamp };
				states.set(target, state);

				// Every funder matches on name and date of birth, so asking without them only wastes the call.
				if (!text(patient.firstName) || !text(patient.lastName) || !isoDateOfBirth(dateOfBirth)) {
					state.checking = false;
					state.error = "Enter the patient's first name, last name and date of birth first. Medicare, DVA "
						+ "and the funds all match on them.";
					return;
				}

				const request = buildRequest(patient, dateOfBirth, kind, target);
				const key = keyOf(patient, dateOfBirth, kind, target);
				TyroAPI.verifyPatientCover(request).then(function (response) {
					state.checkedKey = key;
					state.funder = funderView(patient, kind, target, response.data || {});
					if (state.funder.state === "VERIFIED") {
						stamp(state, target, key, state.funder.responded);
					}
				}).catch(function (error) {
					state.error = ExceptionAPI.serverMessage(error) || "The details could not be checked. Please try again.";
				}).finally(function () {
					state.checking = false;
				});
			},

			// Run each digest by the answer under a part. A stamp stands only for the details it was given
			// against: edited afterwards, it comes off again, back to whatever the record held before.
			reconcile: function (patient, dateOfBirth, kind, policy) {
				const target = targetOf(patient, kind, policy);
				const state = target && states.get(target);
				if (!state) return;
				const key = keyOf(patient, dateOfBirth, kind, target);
				state.stale = !!state.checkedKey && key !== state.checkedKey;
				if (state.stamp && key !== state.stamp.key) {
					target.last_verified_at = state.stamp.previous;
					state.stamp = null;
				}
			},
		};

		// --- The request ---

		function targetOf(patient, kind, policy) {
			if (!patient) return null;
			if (kind === "MEDICARE") return patient.medicare_details || null;
			if (kind === "DVA") return patient.dva_details || null;
			return policy || null;
		}

		function buildRequest(patient, dateOfBirth, kind, target) {
			const request = {
				first_name: text(patient.firstName),
				last_name: text(patient.lastName),
				date_of_birth: isoDateOfBirth(dateOfBirth),
				gender: patient.gender || undefined,
			};
			if (kind === "MEDICARE") {
				request.medicare_card_number = compact(target.card_number);
				request.medicare_irn = text(target.irn) || undefined;
			} else if (kind === "DVA") {
				request.dva_file_number = compact(target.file_number);
			} else {
				request.policies = [{
					health_fund_name: target.health_fund_name,
					membership_number: text(target.membership_number),
					member_reference_number: text(target.member_reference_number) || undefined,
					overseas_cover_type: target.overseas_cover_type || undefined,
				}];
			}
			return request;
		}

		// What an answer was given for: the patient's identity and this part's own details. Anything else on
		// the form can change without making the answer any less true.
		function keyOf(patient, dateOfBirth, kind, target) {
			const identity = [text(patient.firstName), text(patient.lastName), isoDateOfBirth(dateOfBirth), patient.gender || ""];
			if (kind === "MEDICARE") return identity.concat([compact(target.card_number), text(target.irn)]).join("|");
			if (kind === "DVA") return identity.concat([compact(target.file_number)]).join("|");
			return identity.concat([target.health_fund_name, text(target.membership_number), text(target.member_reference_number)]).join("|");
		}

		// --- The answer ---

		function funderView(patient, kind, target, data) {
			if (kind === "MEDICARE") {
				const status = data.medicare_status;
				const differences = [];
				if (status) {
					addDifference(differences, "Card number", target, "card_number", status.suggested_membership_number);
					addDifference(differences, "IRN", target, "irn", status.suggested_card_rank);
					addNameDifferences(differences, patient, status);
				}
				return verdict("Medicare", status, data.medicare_error, differences);
			}

			if (kind === "DVA") {
				const status = data.dva_status;
				const differences = [];
				if (status) {
					addDifference(differences, "File number", target, "file_number", status.suggested_file_number);
					addNameDifferences(differences, patient, status);
				}
				const view = verdict("DVA", status, data.dva_error, differences);
				if (!status) return view;
				// Offered after the verdict rather than counted in it: DVA does not match on the card type, so
				// a form without one has still been verified.
				const cardType = DVA_CARD_TYPES[status.entitlement_code];
				if (cardType && target.card_type !== cardType) {
					view.differences.push(difference("Card type", target, "card_type", target.card_type, cardType));
				}
				if (DVA_CARD_LABELS[status.entitlement_code]) {
					view.note = "DVA holds a " + DVA_CARD_LABELS[status.entitlement_code] + " for this veteran.";
				} else if (status.entitlement_code === "NIL") {
					view.note = "DVA could not say which card this veteran holds. Contact DVA to confirm their eligibility.";
				}
				return view;
			}

			const answer = (data.policies || [])[0] || {};
			const status = answer.status;
			const differences = [];
			if (status) {
				addDifference(differences, "Membership number", target, "membership_number", status.suggested_membership_number);
				addDifference(differences, "Member reference number", target, "member_reference_number", status.suggested_card_rank);
				addNameDifferences(differences, patient, status);
			}
			return verdict(fundName(target.health_fund_name), status, answer.error, differences);
		}

		// Verified means matched on exactly what was sent. A match Medicare made on different details comes
		// back verified with the corrected details beside it, and reading the flag alone would pass details
		// Medicare has just said are wrong - see isAccountVerified() in the claim widget, which reads it the
		// same way.
		function verdict(label, status, error, differences) {
			let state;
			if (error) {
				state = "FAILED";
			} else if (status && status.patient_verified === true && status.update_required !== true && !differences.length) {
				state = "VERIFIED";
			} else if (status && (status.patient_verified === true || differences.length)) {
				state = "UPDATE";
			} else {
				state = "NOT_VERIFIED";
			}
			return {
				label: label,
				state: state,
				message: error || (status && status.status_text) || null,
				differences: differences,
				responded: status && status.responded,
			};
		}

		// Only where the two actually disagree: the funders echo back the account they matched whether or not
		// anything about it changed, and a table of identical rows trains the practice to skip it.
		function addDifference(differences, label, target, field, suggested) {
			if (!text(suggested) || sameDetail(target[field], suggested)) return;
			differences.push(difference(label, target, field, target[field], suggested));
		}

		function addNameDifferences(differences, patient, status) {
			addDifference(differences, "First name", patient, "firstName", status.suggested_first_name);
			addDifference(differences, "Last name", patient, "lastName", status.suggested_last_name);
		}

		function difference(label, target, field, current, suggested) {
			return {
				label: label,
				current: current,
				suggested: suggested,
				// Applied one row at a time and only on request: the practice confirms each with the patient.
				apply: function () {
					target[field] = field === "irn" ? Number(suggested) : suggested;
				},
				isApplied: function () {
					return sameDetail(target[field], suggested);
				},
			};
		}

		function stamp(state, target, key, responded) {
			const previous = state.stamp ? state.stamp.previous : target.last_verified_at;
			target.last_verified_at = isoInstant(responded);
			state.stamp = { key: key, previous: previous };
		}

		// --- Helpers ---

		function fundName(value) {
			const fund = FUND_NAMES.find(function (option) { return option.value === value; });
			return fund ? fund.name : value;
		}

		function isoDateOfBirth(dateOfBirth) {
			const dob = dateOfBirth || {};
			if (!dob.YYYY || !dob.MM || !dob.DD) return null;
			return dob.YYYY + "-" + pad(dob.MM) + "-" + pad(dob.DD);
		}

		function isoInstant(responded) {
			const date = responded ? new Date(responded) : new Date();
			return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
		}

		function sameDetail(current, suggested) {
			return text(current).toUpperCase() === text(suggested).toUpperCase();
		}

		function compact(value) {
			return text(value).replace(/\s/g, "");
		}

		function text(value) {
			return value === null || value === undefined ? "" : String(value).trim();
		}

		function pad(value) {
			return ("0" + value).slice(-2);
		}

		return service;
	},
]);
